import { memo, useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Keyboard, X } from "lucide-react";
import { useAppStore } from "@/core/store/useAppStore";

/* ------------------------------------------------------------------ */
/*  Shortcut definitions                                              */
/* ------------------------------------------------------------------ */

interface ShortcutEntry {
  keys: string[];
  description: string;
}

interface ShortcutGroup {
  category: string;
  shortcuts: ShortcutEntry[];
}

const SHORTCUT_GROUPS: ShortcutGroup[] = [
  {
    category: "Actions",
    shortcuts: [
      { keys: ["⌘", "K"], description: "Command palette" },
      { keys: ["/"], description: "Focus search" },
      { keys: ["?"], description: "Show shortcuts" },
    ],
  },
  {
    category: "Navigation",
    shortcuts: [
      { keys: ["G", "then", "H"], description: "Go to Home / Dashboard" },
      { keys: ["G", "then", "I"], description: "Go to Inbox" },
      { keys: ["G", "then", "D"], description: "Go to Dispatch" },
      { keys: ["G", "then", "W"], description: "Go to Workflows" },
      { keys: ["G", "then", "F"], description: "Go to Fleet" },
      { keys: ["G", "then", "A"], description: "Go to Analytics" },
    ],
  },
];

/* ------------------------------------------------------------------ */
/*  G-then-X navigation map                                           */
/* ------------------------------------------------------------------ */

const G_NAV_MAP: Record<string, string> = {
  h: "/",
  i: "/inbox",
  d: "/dispatch",
  w: "/workflows",
  f: "/fleet",
  a: "/analytics",
};

/* ------------------------------------------------------------------ */
/*  Kbd component                                                     */
/* ------------------------------------------------------------------ */

function Kbd({ children }: { children: React.ReactNode }) {
  return (
    <kbd className="inline-flex items-center justify-center rounded border border-slate-600 bg-slate-700 px-1.5 py-0.5 font-mono text-xs text-slate-300 min-w-[1.5rem] text-center">
      {children}
    </kbd>
  );
}

/* ------------------------------------------------------------------ */
/*  Modal                                                             */
/* ------------------------------------------------------------------ */

function KeyboardShortcutsModalInner({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center animate-backdrop-in"
      style={{ backgroundColor: "rgba(0,0,0,0.6)", backdropFilter: "blur(4px)" }}
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Keyboard shortcuts"
    >
      <div
        className="w-full max-w-md rounded-xl border border-slate-700 bg-slate-800 p-6 shadow-2xl animate-scale-in"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="mb-5 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-600/15">
              <Keyboard size={16} className="text-blue-400" />
            </div>
            <h2 className="text-sm font-semibold text-slate-100">
              Keyboard Shortcuts
            </h2>
          </div>
          <button
            onClick={onClose}
            className="flex h-7 w-7 items-center justify-center rounded-lg text-slate-400 hover:bg-slate-700 hover:text-slate-200 transition-colors"
            aria-label="Close shortcuts"
          >
            <X size={14} />
          </button>
        </div>

        {/* Groups */}
        <div className="space-y-5">
          {SHORTCUT_GROUPS.map((group) => (
            <div key={group.category}>
              <h3 className="mb-2.5 text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                {group.category}
              </h3>
              <div className="space-y-1.5">
                {group.shortcuts.map((shortcut) => (
                  <div
                    key={shortcut.description}
                    className="flex items-center justify-between rounded-lg px-2.5 py-1.5 hover:bg-slate-700/40 transition-colors"
                  >
                    <span className="text-sm text-slate-300">
                      {shortcut.description}
                    </span>
                    <div className="flex items-center gap-1">
                      {shortcut.keys.map((key, i) =>
                        key === "then" ? (
                          <span
                            key={i}
                            className="text-[10px] text-slate-500 px-0.5"
                          >
                            then
                          </span>
                        ) : (
                          <Kbd key={i}>{key}</Kbd>
                        ),
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="mt-5 flex items-center justify-center gap-1 text-[11px] text-slate-600">
          <span>Press</span>
          <Kbd>?</Kbd>
          <span>to toggle this overlay</span>
        </div>
      </div>
    </div>
  );
}

export const KeyboardShortcutsModal = memo(KeyboardShortcutsModalInner);

/* ------------------------------------------------------------------ */
/*  Hook: useKeyboardShortcuts                                        */
/*  Register all global keyboard shortcuts in one place.              */
/* ------------------------------------------------------------------ */

export function useKeyboardShortcuts() {
  const [shortcutsOpen, setShortcutsOpen] = useState(false);
  const navigate = useNavigate();
  const gPendingRef = useRef(false);
  const gTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      const target = e.target as HTMLElement;
      const tag = target.tagName.toLowerCase();
      const isEditable =
        tag === "input" ||
        tag === "textarea" ||
        tag === "select" ||
        target.isContentEditable;

      // Always allow Escape to close shortcuts
      if (e.key === "Escape" && shortcutsOpen) {
        setShortcutsOpen(false);
        return;
      }

      // Don't process shortcuts when focused on editable elements
      if (isEditable) return;

      // ? — toggle shortcuts modal
      if (e.key === "?" || (e.shiftKey && e.key === "/")) {
        e.preventDefault();
        setShortcutsOpen((prev) => !prev);
        return;
      }

      // ⌘K — open command palette
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        useAppStore.getState().setCommandPaletteOpen(true);
        return;
      }

      // / — focus search (only if not in ⌘K palette)
      if (e.key === "/" && !e.metaKey && !e.ctrlKey) {
        e.preventDefault();
        // Trigger ⌘K palette as the search entry point
        useAppStore.getState().setCommandPaletteOpen(true);
        return;
      }

      // G-then-X navigation
      if (gPendingRef.current) {
        const dest = G_NAV_MAP[e.key.toLowerCase()];
        if (dest) {
          e.preventDefault();
          navigate(dest);
          setShortcutsOpen(false);
        }
        gPendingRef.current = false;
        if (gTimerRef.current) {
          clearTimeout(gTimerRef.current);
          gTimerRef.current = null;
        }
        return;
      }

      if (e.key.toLowerCase() === "g" && !e.metaKey && !e.ctrlKey && !e.altKey) {
        e.preventDefault();
        gPendingRef.current = true;
        gTimerRef.current = setTimeout(() => {
          gPendingRef.current = false;
          gTimerRef.current = null;
        }, 1000);
        return;
      }
    },
    [navigate, shortcutsOpen],
  );

  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      if (gTimerRef.current) clearTimeout(gTimerRef.current);
    };
  }, [handleKeyDown]);

  return { shortcutsOpen, setShortcutsOpen };
}
